import React, { useEffect, useState } from 'react';
import { getSettings, updateSettings } from '../services/api';
import { LayoutDashboard, CheckCircle2 } from 'lucide-react';

const ManageSettings = () => {
  const [settings, setSettings] = useState({
    showAboutUs: true,
    showNathTemples: true,
    showOtherTemples: true,
    showBlogs: true,
    showAdAbout: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      const data = await getSettings();
      setSettings(data);
      setLoading(false);
    };
    fetchSettings();
  }, []);

  const handleToggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    await updateSettings(settings);
    setSaving(false);
    setSavedMessage(true);
    setTimeout(() => setSavedMessage(false), 3000);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-stone-200 rounded w-1/4 mb-8"></div>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-16 bg-stone-100 rounded-2xl w-full"></div>
        ))}
      </div>
    );
  }

  const sections = [
    { key: 'showAboutUs', label: 'Show About Us Section', desc: 'Toggle the About Us hero section on the homepage.' },
    { key: 'showNathTemples', label: 'Show Nath Temples Section', desc: 'Toggle the 7 main Nath Temples layout on the homepage.' },
    { key: 'showOtherTemples', label: 'Show Other Temples Section', desc: 'Toggle the dynamic generic templates strip on the homepage.' },
    { key: 'showBlogs', label: 'Show Articles/Blogs Section', desc: 'Toggle the 4 latest articles snippet on the homepage.' },
    { key: 'showAdAbout', label: 'Show Ad Banner on About Page', desc: 'Toggle the Medium Rectangle (MREC) Advertisement on the dedicated /about static page.' },
  ];

  return (
    <div>
      <div className="flex justify-between items-end mb-8 pt-2">
        <div>
          <h1 className="text-3xl font-extrabold text-stone-900 mb-2">Homepage & Layout Settings</h1>
          <p className="text-stone-500">Toggle website sections and advertisements dynamically.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-6 py-2.5 font-bold rounded-xl text-white bg-saffron-600 hover:bg-saffron-700 transition shadow-md shadow-saffron-600/20 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {savedMessage && (
        <div className="mb-6 p-4 bg-green-50 text-green-700 border border-green-200 rounded-xl flex items-center">
          <CheckCircle2 className="w-5 h-5 mr-2" /> Settings updated successfully!
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-stone-200 bg-stone-50 flex items-center">
          <LayoutDashboard className="w-5 h-5 text-saffron-600 mr-2" />
          <h2 className="text-lg font-bold text-stone-800">Website UI Display Options</h2>
        </div>
        
        <div className="divide-y divide-stone-100">
          {sections.map(({ key, label, desc }) => (
            <div key={key} className="p-6 flex items-center justify-between hover:bg-stone-50 transition-colors">
              <div className="pr-6">
                <h3 className="font-bold text-stone-800 text-lg">{label}</h3>
                <p className="text-sm text-stone-500 mt-1">{desc}</p>
              </div>
              <button
                onClick={() => handleToggle(key)}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-saffron-500 focus:ring-offset-2 ${
                  settings[key] ? 'bg-saffron-600' : 'bg-stone-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    settings[key] ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ManageSettings;
